import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setSessionToken, getSessionToken } from "@/lib/api";

/**
 * Session-hydration-safe auth provider.
 *
 * Rules (do not weaken without reading this comment):
 *   1. `initialSessionFetched` starts false. While false, NO redirect decision
 *      may be made anywhere in the app. Consumers must render a spinner.
 *   2. It flips to true exactly once — after the first `/auth/me` probe
 *      resolves (success or 401). From then on `user` is the single source
 *      of truth.
 *   3. If the URL carries `#session_id=...` we defer the probe and let
 *      `AuthCallback` handle the exchange; it calls `setUser()` directly
 *      and then flips the gate.
 *   4. The `oak_is_logged_in` localStorage flag + `oak_session_token` Bearer
 *      fallback make sign-in survive cookie-less browsers (Safari iOS,
 *      Brave, privacy modes).
 */
const AuthContext = createContext(null);
const LOGGED_IN_FLAG = "oak_is_logged_in";

function setLoggedInFlag(v) {
  try {
    if (v) localStorage.setItem(LOGGED_IN_FLAG, "1");
    else localStorage.removeItem(LOGGED_IN_FLAG);
  } catch {}
}

function hasLoggedInFlag() {
  try { return localStorage.getItem(LOGGED_IN_FLAG) === "1"; } catch { return false; }
}

export function AuthProvider({ children }) {
  const [user, _setUser] = useState(null);
  const [initialSessionFetched, setInitialSessionFetched] = useState(false);

  // Wrapper so setting the user also keeps the logged-in flag in sync.
  const setUser = useCallback((u) => {
    _setUser(u);
    setLoggedInFlag(!!u);
  }, []);

  const fetchSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      _setUser(data);
      setLoggedInFlag(true);
    } catch (err) {
      _setUser(null);
      // Only clear the flag on a definitive 401/403. Transient network
      // failures shouldn't log the user out — the next visit will retry.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setLoggedInFlag(false);
        setSessionToken(null);
      }
    } finally {
      setInitialSessionFetched(true);
    }
  }, []);

  useEffect(() => {
    // If we're returning from the Emergent OAuth redirect, let AuthCallback
    // own the /auth/session exchange and the gate flip. Do NOT probe /auth/me
    // in parallel — that would race the cookie write and cause the loop.
    const hasHash = typeof window !== "undefined"
      && window.location.hash
      && window.location.hash.includes("session_id=");
    if (hasHash) {
      // Stay in the loading state; AuthCallback will call `setUser` then
      // `markSessionFetched` after the exchange completes.
      return;
    }
    fetchSession();
  }, [fetchSession]);

  const markSessionFetched = useCallback(() => setInitialSessionFetched(true), []);

  const logout = async () => {
    try { await api.post("/auth/logout", {}); } catch {}
    setSessionToken(null);
    setLoggedInFlag(false);
    _setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        initialSessionFetched,
        markSessionFetched,
        logout,
        hasLoggedInFlag: hasLoggedInFlag(),
        hasLocalToken: !!getSessionToken(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
